/**
 * MilestonesPanel
 *
 * Dropdown panel for managing milestones, opened from the ProjectsGantt toolbar.
 * Allows viewing, creating, editing, and deleting milestones grouped by space.
 * Shows associated tasks per milestone with expand/collapse and inline task search.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Diamond, Plus, Trash2, Calendar, Check, Pencil, X, ChevronRight } from 'lucide-react';
import { InlineDatePicker } from '@/components/shared/inline-editors';
import clsx from 'clsx';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { supabase } from '@/lib/supabaseClient';
import {
  createMilestone,
  updateMilestone,
  deleteMilestone,
  getTasksByMilestone,
  assignTaskToMilestone,
  type MilestoneWithProgress,
  type MilestoneStatus,
} from '@/db/milestones';
import { isCompletedStatus } from '@/constants/taskStatuses';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MilestoneTask = {
  id: string;
  name: string;
  status: string;
  dueDate: number | null;
  assignees: string[];
};

export interface MilestonesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  spaces: Array<{ id: string; name: string }>;
  onMilestonesChange?: (milestones: MilestoneWithProgress[]) => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  allTasks?: Array<{ id: string; name: string; spaceName: string; milestoneId: string | null }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'text-accent-emerald';
    case 'open':
      return 'text-accent-violet';
    case 'in_progress':
      return 'text-accent-blue';
    case 'missed':
      return 'text-accent-rose';
    default:
      return 'text-text-quaternary';
  }
}

function statusBgColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-accent-emerald/15';
    case 'open':
      return 'bg-accent-violet/15';
    case 'in_progress':
      return 'bg-accent-blue/15';
    case 'missed':
      return 'bg-accent-rose/15';
    default:
      return 'bg-surface-tertiary';
  }
}

function taskDotColor(status: string): string {
  if (isCompletedStatus(status)) return 'bg-accent-emerald';
  if (status === 'in_progress') return 'bg-accent-blue';
  return 'bg-text-quaternary';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MilestonesPanel({ isOpen, onClose, spaces, onMilestonesChange, anchorRef, allTasks }: MilestonesPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [milestones, setMilestones] = useState<MilestoneWithProgress[]>([]);
  const [loading, setLoading] = useState(false);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState<number | null>(null);
  const [newSpaceId, setNewSpaceId] = useState('');
  const [creating, setCreating] = useState(false);
  const [spaceDropdownOpen, setSpaceDropdownOpen] = useState(false);
  const [spaceSearch, setSpaceSearch] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDate, setEditDate] = useState<number | null>(null);

  // Task expansion state
  const [expandedMilestoneId, setExpandedMilestoneId] = useState<string | null>(null);
  const [milestoneTasks, setMilestoneTasks] = useState<Record<string, MilestoneTask[]>>({});
  const [loadingTasks, setLoadingTasks] = useState<string | null>(null);

  // Task search state
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [taskSearchDropdownOpen, setTaskSearchDropdownOpen] = useState(false);

  // Refs for dropdowns
  const spaceDropdownRef = useRef<HTMLDivElement>(null);
  const taskSearchRef = useRef<HTMLDivElement>(null);

  // Close space dropdown on outside click
  useEffect(() => {
    if (!spaceDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (spaceDropdownRef.current && !spaceDropdownRef.current.contains(e.target as Node)) {
        setSpaceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [spaceDropdownOpen]);

  // Close task search dropdown on outside click
  useEffect(() => {
    if (!taskSearchDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (taskSearchRef.current && !taskSearchRef.current.contains(e.target as Node)) {
        setTaskSearchDropdownOpen(false);
        setTaskSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [taskSearchDropdownOpen]);

  // Position panel below anchor and handle outside clicks
  useEffect(() => {
    if (!isOpen) return;
    // Calculate position from anchor
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    // Outside click handler (delayed to avoid catching the opening click)
    let handler: ((e: MouseEvent) => void) | null = null;
    const timer = setTimeout(() => {
      handler = (e: MouseEvent) => {
        if (panelRef.current && !panelRef.current.contains(e.target as Node) &&
            anchorRef?.current && !anchorRef.current.contains(e.target as Node)) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handler);
    }, 50);
    return () => { clearTimeout(timer); if (handler) document.removeEventListener('mousedown', handler); };
  }, [isOpen, onClose, anchorRef]);

  // Fetch ALL milestones in one query instead of looping per space
  const fetchMilestones = useCallback(async () => {
    if (spaces.length === 0) return;
    setLoading(true);
    try {
      const spaceIds = spaces.map(s => s.id);
      const { data: rows } = await supabase
        .from('milestones')
        .select('*')
        .in('space_id', spaceIds)
        .order('target_date', { ascending: true, nullsFirst: false });

      if (!rows || rows.length === 0) {
        setMilestones([]);
        onMilestonesChange?.([]);
        return;
      }

      // Fetch task counts for these milestones in one query
      const milestoneIds = rows.map(r => r.id);
      const { data: tasks } = await supabase
        .from('tasks')
        .select('milestone_id, status')
        .in('milestone_id', milestoneIds);

      // Build progress map
      const progressMap = new Map<string, { total: number; completed: number }>();
      milestoneIds.forEach(id => progressMap.set(id, { total: 0, completed: 0 }));
      (tasks || []).forEach(t => {
        const p = progressMap.get(t.milestone_id);
        if (p) { p.total++; if (isCompletedStatus(t.status)) p.completed++; }
      });

      const results: MilestoneWithProgress[] = rows.map(r => {
        const p = progressMap.get(r.id) || { total: 0, completed: 0 };
        return {
          id: r.id,
          spaceId: r.space_id,
          name: r.name,
          description: r.description || null,
          targetDate: r.target_date || null,
          status: (r.status as MilestoneStatus) || 'open',
          color: r.color || '#6366f1',
          orderIndex: r.order_index || 0,
          createdBy: r.created_by || null,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
          totalTasks: p.total,
          completedTasks: p.completed,
          progressPercent: p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0,
        };
      });

      setMilestones(results);
      onMilestonesChange?.(results);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  // deps: only re-create when space count changes (avoid infinite loop from unstable spaces array ref)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces.length]);

  useEffect(() => {
    if (isOpen) {
      fetchMilestones();
    }
  }, [isOpen, fetchMilestones]);

  // Group milestones by space
  const grouped = milestones.reduce<Record<string, MilestoneWithProgress[]>>((acc, m) => {
    if (!acc[m.spaceId]) acc[m.spaceId] = [];
    acc[m.spaceId].push(m);
    return acc;
  }, {});

  const getSpaceName = (spaceId: string) =>
    spaces.find((s) => s.id === spaceId)?.name ?? 'Sconosciuto';

  const selectedSpaceName = newSpaceId ? getSpaceName(newSpaceId) : '';

  // Create milestone
  const handleCreate = async () => {
    if (!newName.trim() || !newSpaceId) return;
    setCreating(true);
    try {
      const targetDate = newDate ?? undefined;
      await createMilestone({
        spaceId: newSpaceId,
        name: newName.trim(),
        targetDate,
      });
      setNewName('');
      setNewDate(null);
      setNewSpaceId('');
      setShowCreate(false);
      await fetchMilestones();
    } catch {
      // silently fail
    } finally {
      setCreating(false);
    }
  };

  // Delete milestone
  const handleDelete = async (id: string) => {
    try {
      await deleteMilestone(id);
      if (expandedMilestoneId === id) {
        setExpandedMilestoneId(null);
      }
      await fetchMilestones();
    } catch {
      // silently fail
    }
  };

  // Start editing
  const startEdit = (m: MilestoneWithProgress) => {
    setEditingId(m.id);
    setEditName(m.name);
    setEditDate(m.targetDate ?? null);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    try {
      const targetDate = editDate;
      await updateMilestone(editingId, {
        name: editName.trim(),
        targetDate,
      });
      setEditingId(null);
      await fetchMilestones();
    } catch {
      // silently fail
    }
  };

  // Toggle milestone task expansion
  const toggleMilestoneTasks = async (milestoneId: string) => {
    if (expandedMilestoneId === milestoneId) {
      setExpandedMilestoneId(null);
      setTaskSearchQuery('');
      setTaskSearchDropdownOpen(false);
      return;
    }
    setExpandedMilestoneId(milestoneId);
    setTaskSearchQuery('');
    setTaskSearchDropdownOpen(false);

    // Fetch tasks if not cached
    if (!milestoneTasks[milestoneId]) {
      setLoadingTasks(milestoneId);
      try {
        const tasks = await getTasksByMilestone(milestoneId);
        setMilestoneTasks(prev => ({ ...prev, [milestoneId]: tasks }));
      } catch {
        setMilestoneTasks(prev => ({ ...prev, [milestoneId]: [] }));
      } finally {
        setLoadingTasks(null);
      }
    }
  };

  // Refresh tasks for a milestone
  const refreshMilestoneTasks = async (milestoneId: string) => {
    try {
      const tasks = await getTasksByMilestone(milestoneId);
      setMilestoneTasks(prev => ({ ...prev, [milestoneId]: tasks }));
    } catch {
      // silently fail
    }
  };

  // Unassign task from milestone
  const handleUnassignTask = async (taskId: string, milestoneId: string) => {
    try {
      await assignTaskToMilestone(taskId, null);
      await refreshMilestoneTasks(milestoneId);
      await fetchMilestones();
    } catch {
      // silently fail
    }
  };

  // Assign task to milestone
  const handleAssignTask = async (taskId: string, milestoneId: string) => {
    try {
      await assignTaskToMilestone(taskId, milestoneId);
      setTaskSearchQuery('');
      setTaskSearchDropdownOpen(false);
      await refreshMilestoneTasks(milestoneId);
      await fetchMilestones();
    } catch {
      // silently fail
    }
  };

  // Filter available tasks for search — match by space name since task.spaceId is actually a container/list ID
  const getSearchResults = (milestoneId: string, spaceId: string) => {
    if (!allTasks || !taskSearchQuery.trim()) return [];
    const spaceName = spaces.find(s => s.id === spaceId)?.name || '';
    const query = taskSearchQuery.toLowerCase();
    return allTasks
      .filter(t =>
        t.spaceName === spaceName &&
        t.milestoneId === null &&
        t.name.toLowerCase().includes(query)
      )
      .slice(0, 8);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={panelRef}
      className="fixed w-96 max-h-[500px] bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-50 animate-fade-in"
      style={{ top: panelPos.top, right: panelPos.right }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-primary">
        <div className="flex items-center gap-1.5">
          <Diamond className="w-3.5 h-3.5 text-accent-violet" />
          <span className="text-[11px] font-semibold text-text-primary">Milestone</span>
          <span className="text-[10px] text-text-quaternary">({milestones.length})</span>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className={clsx(
            'p-1 rounded transition-colors',
            showCreate
              ? 'text-accent-violet bg-accent-violet/10'
              : 'text-text-quaternary hover:text-text-tertiary',
          )}
          title="Nuova milestone"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="px-3 py-2 border-b border-border-primary bg-surface-tertiary/30 space-y-1.5">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome milestone..."
            className="w-full px-2 py-1 rounded bg-surface-primary border border-border-primary text-[11px] text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent-violet/50"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <div className="flex gap-1.5 items-center">
            <div className="flex-1">
              <InlineDatePicker
                value={newDate}
                onChange={setNewDate}
                label="Data target"
                showTime={false}
                size="sm"
              />
            </div>
            {/* Searchable space dropdown */}
            <div className="flex-1 relative" ref={spaceDropdownRef}>
              <button
                type="button"
                onClick={() => { setSpaceDropdownOpen(!spaceDropdownOpen); setSpaceSearch(''); }}
                className={clsx(
                  'w-full px-2 py-1 rounded bg-surface-primary border text-[11px] text-left truncate transition-colors focus:outline-none',
                  spaceDropdownOpen ? 'border-accent-violet/50' : 'border-border-primary',
                  newSpaceId ? 'text-text-primary' : 'text-text-quaternary',
                )}
              >
                {selectedSpaceName || 'Progetto...'}
              </button>
              {spaceDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-50 overflow-hidden">
                  <div className="px-2 py-1.5 border-b border-border-primary">
                    <input
                      type="text"
                      value={spaceSearch}
                      onChange={(e) => setSpaceSearch(e.target.value)}
                      placeholder="Cerca progetto..."
                      className="w-full px-2 py-1 rounded bg-surface-primary border border-border-primary text-[11px] text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent-violet/50"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-[140px] overflow-y-auto py-0.5">
                    {spaces
                      .filter(s => !spaceSearch || s.name.toLowerCase().includes(spaceSearch.toLowerCase()))
                      .map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => { setNewSpaceId(s.id); setSpaceDropdownOpen(false); }}
                          className={clsx(
                            'w-full px-2 py-1 text-[11px] text-left truncate transition-colors',
                            s.id === newSpaceId ? 'bg-accent-violet/10 text-accent-violet' : 'text-text-primary hover:bg-surface-hover',
                          )}
                        >
                          {s.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleCreate}
            disabled={!newName.trim() || !newSpaceId || creating}
            className={clsx(
              'w-full py-1 rounded text-[10px] font-semibold transition-colors',
              newName.trim() && newSpaceId
                ? 'bg-accent-violet text-white hover:bg-accent-violet/90'
                : 'bg-surface-tertiary text-text-quaternary cursor-not-allowed',
            )}
          >
            {creating ? 'Creazione...' : 'Crea'}
          </button>
        </div>
      )}

      {/* Milestones list */}
      <div className="max-h-[360px] overflow-y-auto">
        {loading && milestones.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <span className="text-[11px] text-text-quaternary">Caricamento...</span>
          </div>
        ) : milestones.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <Diamond className="w-5 h-5 text-text-quaternary mx-auto mb-2" />
            <p className="text-[11px] text-text-quaternary">Nessuna milestone</p>
          </div>
        ) : (
          Object.entries(grouped).map(([spaceId, spaceMilestones]) => (
            <div key={spaceId}>
              {/* Space group header */}
              <div className="px-3 py-1.5 bg-surface-tertiary/20 border-b border-border-primary/30">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-text-quaternary">
                  {getSpaceName(spaceId)}
                </span>
              </div>

              {/* Milestones in this space */}
              {spaceMilestones.map((m) => {
                const isExpanded = expandedMilestoneId === m.id;
                const tasks = milestoneTasks[m.id] || [];
                const isLoadingTasks = loadingTasks === m.id;

                return (
                  <div
                    key={m.id}
                    className="border-b border-border-primary/30"
                  >
                    <div className="group px-3 py-2 hover:bg-surface-hover/50 transition-colors">
                      {editingId === m.id ? (
                        /* Edit mode */
                        <div className="space-y-1.5">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full px-2 py-1 rounded bg-surface-primary border border-border-primary text-[11px] text-text-primary focus:outline-none focus:border-accent-violet/50"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                          />
                          <div className="flex gap-1.5 items-center">
                            <div className="flex-1">
                              <InlineDatePicker
                                value={editDate}
                                onChange={setEditDate}
                                showTime={false}
                                size="sm"
                              />
                            </div>
                            <button
                              onClick={handleSaveEdit}
                              className="p-1 rounded bg-accent-violet text-white hover:bg-accent-violet/90 transition-colors"
                            >
                              <Check className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="p-1 rounded text-text-quaternary hover:text-text-secondary hover:bg-surface-tertiary transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* View mode */
                        <div>
                          <div className="flex items-center gap-1.5">
                            <Diamond
                              className={clsx('w-3 h-3 shrink-0', statusColor(m.status))}
                              style={m.color ? { color: m.color } : undefined}
                            />
                            <span className="text-[11px] font-medium text-text-primary truncate flex-1">
                              {m.name}
                            </span>
                            <button
                              onClick={() => startEdit(m)}
                              className="p-0.5 rounded text-text-quaternary hover:text-text-secondary opacity-0 group-hover:opacity-100 transition-all"
                              title="Modifica"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDelete(m.id)}
                              className="p-0.5 rounded text-text-quaternary hover:text-accent-rose opacity-0 group-hover:opacity-100 transition-all"
                              title="Elimina"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-1 ml-[18px]">
                            {m.targetDate && (
                              <span className="flex items-center gap-0.5 text-[10px] text-text-tertiary">
                                <Calendar className="w-2.5 h-2.5" />
                                {format(new Date(m.targetDate), 'd MMM yyyy', { locale: it })}
                              </span>
                            )}
                            <span className={clsx('text-[10px] font-medium px-1.5 py-0.5 rounded-full', statusBgColor(m.status), statusColor(m.status))}>
                              {m.completedTasks}/{m.totalTasks} task
                            </span>
                          </div>
                          {/* Progress bar */}
                          {m.totalTasks > 0 && (
                            <div className="ml-[18px] mt-1 h-1 rounded-full bg-surface-tertiary overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${m.progressPercent}%`,
                                  backgroundColor: m.progressPercent >= 100
                                    ? 'rgb(var(--accent-emerald))'
                                    : m.progressPercent >= 50
                                      ? 'rgb(var(--accent-amber))'
                                      : 'rgb(var(--accent-violet))',
                                }}
                              />
                            </div>
                          )}
                          {/* Expandable tasks section */}
                          <button
                            onClick={() => toggleMilestoneTasks(m.id)}
                            className="flex items-center gap-1 mt-1.5 ml-[18px] text-[10px] text-text-tertiary hover:text-text-secondary transition-colors"
                          >
                            <ChevronRight
                              className={clsx(
                                'w-2.5 h-2.5 transition-transform',
                                isExpanded && 'rotate-90',
                              )}
                            />
                            <span>{m.totalTasks} task associati</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Expanded tasks list */}
                    {isExpanded && editingId !== m.id && (
                      <div className="px-3 pb-2 bg-surface-tertiary/20">
                        {isLoadingTasks ? (
                          <div className="py-2 text-center">
                            <span className="text-[10px] text-text-quaternary">Caricamento task...</span>
                          </div>
                        ) : tasks.length === 0 ? (
                          <div className="py-2 text-center">
                            <span className="text-[10px] text-text-quaternary">Nessun task associato</span>
                          </div>
                        ) : (
                          <div className="space-y-0.5">
                            {tasks.map((task) => (
                              <div
                                key={task.id}
                                className="group/task flex items-center gap-1.5 py-1 px-1.5 rounded hover:bg-surface-hover/50 transition-colors"
                              >
                                <div className={clsx('w-1.5 h-1.5 rounded-full shrink-0', taskDotColor(task.status))} />
                                <span className="text-[10px] text-text-secondary truncate flex-1">
                                  {task.name}
                                </span>
                                {task.dueDate && (
                                  <span className="text-[10px] text-text-quaternary shrink-0">
                                    {format(new Date(task.dueDate), 'd MMM', { locale: it })}
                                  </span>
                                )}
                                <button
                                  onClick={() => handleUnassignTask(task.id, m.id)}
                                  className="p-0.5 rounded text-text-quaternary hover:text-accent-rose opacity-0 group-hover/task:opacity-100 transition-all shrink-0"
                                  title="Rimuovi"
                                >
                                  <X className="w-2.5 h-2.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add task search input */}
                        <div className="relative mt-1.5" ref={taskSearchRef}>
                          <input
                            type="text"
                            value={taskSearchQuery}
                            onChange={(e) => {
                              setTaskSearchQuery(e.target.value);
                              setTaskSearchDropdownOpen(e.target.value.trim().length > 0);
                            }}
                            placeholder="Aggiungi task..."
                            className="w-full px-2 py-1 rounded bg-surface-primary border border-border-primary text-[10px] text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent-violet/50"
                          />
                          {taskSearchDropdownOpen && taskSearchQuery.trim() && (
                            <div className="absolute bottom-full left-0 right-0 mb-0.5 bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-[60] py-0.5 max-h-[120px] overflow-y-auto">
                              {(() => {
                                const results = getSearchResults(m.id, m.spaceId);
                                if (results.length === 0) {
                                  return (
                                    <div className="px-2 py-1.5 text-[10px] text-text-quaternary text-center">
                                      Nessun task trovato
                                    </div>
                                  );
                                }
                                return results.map((t) => (
                                  <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => handleAssignTask(t.id, m.id)}
                                    className="w-full px-2 py-1 text-[10px] text-text-primary text-left truncate hover:bg-surface-hover transition-colors"
                                  >
                                    {t.name}
                                  </button>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>,
    document.body,
  );
}
